import { GetServerSideProps } from 'next'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { Trans, useTranslation } from 'next-i18next'
import getEMTServerSideProps, { EMTServerSideProps } from '@/helpers/getEMTServerSideProps'
import { useRouter } from 'next/router'

import NextLink from 'next/link'
import NextImage from 'next/image'
import Box from '@mui/material/Box'
import { SEO, withNoAuthRoute, withNoAuthRouteProps } from '@/components'
import { EmtCircularProgress, EmtPaper, EmtTypography, EmtIcon } from 'emt-components-v2'
import { useIsResponsive } from '@/hooks'
import NextMuiLink from '@/components/NextMuiLink'
import { Routes } from '@/assets/configs'
import { appendParamsIfNeeded, getFirstQueryParam } from '@/utils'
import SimpleLayout from '@/components/EMT/SimpleLayout'
import { VeterinaryGuerricComponent } from '@/components/pages/pet-sitter/reservations/NewRequest/VeterinaryComponent'
import AppRatingBox from '@/components/pages/sign-up-call-request/AppRatingBox'

import VariantImages from '@/assets/VariantImages'

import { UrlParamType } from '@/modules/utils'
import { useEffect } from 'react'

import { useNewRequestCartStore } from '@/store/newRequestCartStore'

type Props = withNoAuthRouteProps & EMTServerSideProps

const SignUpPage: React.FC<Props> = ({ url, title }) => {
  const { t } = useTranslation('connection')
  const { t: tpremium } = useTranslation('premium')
  const { t: tp } = useTranslation('petsitter')
  const router = useRouter()
  const isPetsittingMode = usePetsittingMode.getState().isPetsittingMode

  const selectedService = useNewRequestCartStore((s) => s.selectedService)

  const fromQuery = getFirstQueryParam(router.query.from)

  const [isMobile] = useIsResponsive()

  useEffect(() => {
    localStorage.clear()
  }, [])

  const rightContent = (
    <EmtCircularProgress
      stepTitle={t('stepTitle') as string}
      color="white"
      value={0}
      currentStep={1}
      totalStep={3}
      currentStepTitle={t('sign-up.stepTitle') as string}
    />
  )

  return (
    <>
      <SEO url={`${url}`} subtitle={title} />

      <SimpleLayout
        title={isPetsittingMode ? t('sign-up.titlePetsitting') : title}
        rightContent={!isPetsittingMode ? rightContent : undefined}
        sx={{ overflow: 'hidden' }}
        goBack={
          isPetsittingMode && isMobile
            ? () =>
                fromQuery === 'petsitter-search'
                  ? router.push({
                      pathname: Routes.PetSitter.Search,
                      query: {
                        service: selectedService,
                      },
                    })
                  : router.push(Routes.Connection.WelcomePetsitting)
            : undefined
        }
      >
        <EmtTypography variant="body1">
          <Trans
            t={t}
            i18nKey={isPetsittingMode ? 'sign-up.createProfilePetsitting' : 'sign-up.createProfile'}
            components={{
              Bold: isMobile ? <strong style={{ display: 'block', marginTop: '10px' }} /> : <strong />,
            }}
          />
        </EmtTypography>
        <Box
          sx={{
            display: isMobile ? 'flex' : 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            flexDirection: isMobile ? 'column' : 'initial',
            gap: '15px',
          }}
        >
          <NextLink
            href={appendParamsIfNeeded(Routes.Connection.SignUp.Details, { type: UrlParamType.Owner })}
            passHref
            legacyBehavior
          >
            <EmtPaper
              variant="emt"
              sx={{
                display: 'flex',
                flexDirection: !isMobile ? 'column' : 'row',
                textAlign: !isMobile ? 'center' : 'left',
                alignItems: 'center',
                padding: '20px!important',
                gap: '15px',
                border: '2px solid transparent',
                transition: 'border-color 0.1s ease',
                width: '100%',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <NextImage
                src={isPetsittingMode ? VariantImages.SignUpDogOwnerPurple : VariantImages.SignUpDogOwner}
                alt={t('sign-up.dogownerImageAlt')}
                quality={100}
                height={78}
                width={78}
                style={{ maxHeight: '8vh', height: '78px', width: 'auto' }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <EmtTypography variant="body1" fontWeight="bold" lineHeight="1.32">
                  {t('sign-up.dogowner')}
                </EmtTypography>
                <EmtTypography variant="body2" lineHeight="1" color="#77757F">
                  {t('sign-up.dogownerInfo')}
                </EmtTypography>
              </Box>
            </EmtPaper>
          </NextLink>
          <Box
            sx={{
              display: 'flex',
              gap: '15px',
              alignItems: 'stretch',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {!isPetsittingMode && (
              <NextLink
                href={appendParamsIfNeeded(Routes.Connection.SignUp.Details, { type: UrlParamType.Borrower })}
                passHref
                legacyBehavior
              >
                <EmtPaper
                  variant="emt"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: !isMobile ? 'column' : 'row',
                    textAlign: !isMobile ? 'center' : 'left',
                    padding: '20px!important',
                    gap: '15px',
                    border: '2px solid transparent',
                    width: '100%',
                    transition: 'border-color 0.1s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <NextImage
                    src={VariantImages.SignUpBorrower}
                    alt={t('sign-up.borrowerImageAlt')}
                    quality={100}
                    height={78}
                    width={78}
                    style={{ maxHeight: '8vh', height: '78px', width: 'auto' }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <EmtTypography variant="body1" fontWeight="bold" lineHeight="1.32">
                      {t('sign-up.borrower')}
                    </EmtTypography>
                    <EmtTypography variant="body2" lineHeight="1" color="#77757F">
                      {t('sign-up.borrowerInfo')}
                    </EmtTypography>
                  </Box>
                </EmtPaper>
              </NextLink>
            )}
            {isPetsittingMode && (
              <NextLink
                href={appendParamsIfNeeded(Routes.Connection.SignUp.Details, { type: UrlParamType.Pro })}
                passHref
                legacyBehavior
              >
                <EmtPaper
                  variant="emt"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '20px!important',
                    gap: '15px',
                    border: '2px solid transparent',
                    transition: 'border-color 0.1s ease',
                    width: '100%',
                    flexDirection: !isMobile ? 'column' : 'row',
                    textAlign: !isMobile ? 'center' : 'left',

                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <NextImage
                    src={VariantImages.SignUpPetSitter}
                    alt={t('sign-up.dogownerImageAlt')}
                    quality={100}
                    height={78}
                    width={78}
                    style={{ maxHeight: '8vh', height: '78px', width: 'auto' }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <EmtTypography variant="body1" fontWeight="bold" lineHeight="1.32">
                      <>Pet Sitter</>
                    </EmtTypography>
                    <EmtTypography variant="body2" lineHeight="1" color="#77757F">
                      {t('sign-up.petsitterInfo')}
                    </EmtTypography>
                  </Box>
                </EmtPaper>
              </NextLink>
            )}
          </Box>
        </Box>
        <EmtTypography variant="body2" sx={{ marginTop: '15px', textAlign: 'center' }}>
          <NextMuiLink color="#77757F" href={Routes.Connection.SignIn} title={t('sign-up.loginButtonAlt') as string}>
            {t('sign-up.loginButton')}
          </NextMuiLink>
        </EmtTypography>

        {isPetsittingMode && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                backgroundColor: '#fff',
                borderRadius: '16px',
                padding: '16px',
              }}
            >
              <EmtTypography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: '1.2' }}>
                <Trans
                  t={tp}
                  i18nKey={'search.testInfoBoxText'}
                  components={{
                    IconSkill: (
                      <EmtIcon
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#0030cc',
                          transform: 'translateY(1px)',
                        }}
                        className="emt-knowledge"
                      />
                    ),
                    IconPsychological: (
                      <EmtIcon
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#C80068',
                          transform: 'translateY(1px)',
                        }}
                        className="emt-psychology"
                      />
                    ),
                    strong: <strong />,
                    BlueInseparables: (
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: '#0030cc',
                          fontWeight: '700',
                          lineHeight: '0',
                          height: '0px',
                          transform: 'translateY(1px)',
                        }}
                      />
                    ),
                    PinkInseparables: (
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: '#C80068',
                          fontWeight: '700',
                          lineHeight: '0',
                          height: '0px',
                          transform: 'translateY(1px)',
                        }}
                      />
                    ),
                  }}
                />
              </EmtTypography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '5px',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#E8F8F4',
                    borderRadius: '100vmax',
                    padding: '3px 3px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                  }}
                >
                  <EmtIcon
                    className="emt-tick-circle-1"
                    sx={{
                      fontSize: '12px',
                      color: '#18BA92',
                    }}
                  />
                  <EmtTypography variant="body2" color="#18BA92" fontWeight="600" fontSize="10px" lineHeight="1">
                    {t('sign-up.infoBox.chip1')}
                  </EmtTypography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#E8F8F4',
                    borderRadius: '100vmax',
                    padding: '3px 3px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                  }}
                >
                  <EmtIcon
                    className="emt-tick-circle-1"
                    sx={{
                      fontSize: '12px',
                      color: '#18BA92',
                    }}
                  />
                  <EmtTypography variant="body2" color="#18BA92" fontWeight="600" fontSize="10px" lineHeight="1">
                    {t('sign-up.infoBox.chip2')}
                  </EmtTypography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#E8F8F4',
                    borderRadius: '100vmax',
                    padding: '3px 3px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                  }}
                >
                  <EmtIcon
                    className="emt-tick-circle-1"
                    sx={{
                      fontSize: '12px',
                      color: '#18BA92',
                    }}
                  />
                  <EmtTypography variant="body2" color="#18BA92" fontWeight="600" fontSize="10px" lineHeight="1">
                    {t('sign-up.infoBox.chip3')}
                  </EmtTypography>
                </Box>
              </Box>
            </Box>
            <AppRatingBox />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', padding: '0 18px' }}>
              <EmtTypography variant="body2" color="textSecondary" fontWeight="600" sx={{ textAlign: 'center' }}>
                Recommandé par les médias de confiance :
              </EmtTypography>
              <NextImage
                src={VariantImages.Medias2}
                alt="sitter footer"
                width={327}
                height={15}
                style={{ display: 'block', filter: 'grayscale(100%)' }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '15px', flexDirection: isMobile ? 'column' : 'row' }}>
              <Box
                sx={{
                  backgroundColor: '#fff',
                  padding: '20px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <NextImage src={VariantImages.Premium30MillionsAmis} alt="partenaire 30 millions d'amis" />
                <EmtTypography variant="body2">
                  <b>{tpremium('baseDogownerPetsitting.sidebar.partner30Millions')}</b>
                </EmtTypography>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#fff',
                  padding: '20px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <NextImage src={VariantImages.PremiumHandiChiens} alt="partenaire handi'chiens" />
                <EmtTypography variant="body2">
                  <b>{tpremium('baseDogownerPetsitting.sidebar.partnerHandiChiens')}</b>
                </EmtTypography>
              </Box>
            </Box>
            <Box sx={{ paddingBottom: '100px' }}>
              <VeterinaryGuerricComponent />
            </Box>
          </Box>
        )}
      </SimpleLayout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = getEMTServerSideProps({
  namespaces: ['common', 'connection', 'petsitter', 'premium'],
  i18nTitleNs: 'connection',
  i18nTitleKey: 'sign-up.title',
})

export default withNoAuthRoute()(SignUpPage)
